import React, {useCallback, useEffect, useRef, useState} from "react";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TrendingUpOutlinedIcon from "@material-ui/icons/TrendingUpOutlined";
import CalendarTodayOutlinedIcon from "@material-ui/icons/CalendarTodayOutlined";
import CreditCardOutlinedIcon from "@material-ui/icons/CreditCardOutlined";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import FilterListIcon from "@material-ui/icons/FilterList";
import Divider from "@material-ui/core/Divider";
import TableComponent from 'components/shared/TableComponent';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import BuyCreditsDialog from '../../DialogForms/BuyCreditsDialog';
import CancelSubscriptionDialog from '../../DialogForms/CancelSubscriptionDialog';
import ChangePlanDialog from '../../DialogForms/ChangePlanDialog';
import ViewReportCostDialog from '../../DialogForms/ViewReportCostDialog';
import FreeTrialWelcomeModal from 'components/FreeTrialWelcome/FreeTrialWelcomeModal';
import {Icon} from 'styles'
import styles from '../profile.module.scss';
import strings from 'strings';
import StripeCheckoutBlade from "./stripeCheckoutBlade";
import { cancelSubscription, subscriptionDetails, switchSubscription, getCreditPackages } from "../../../../actions/client";
import {CircularProgress} from "@material-ui/core";
import {centsToUSD} from "../../../../tools/formatters";
import AuthService from "../../../../tools/AuthService";
import { TRIAL_INCLUDED_CREDITS } from "constants/plans"


export function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

export default function BillingTab({
  tiers,
  handleTierChange,
  refreshSignal
}) {
  const [buyCreditsDialogOpen, setBuyCreditsDialogOpen] = useState(false);
  const [buyCreditsAmount, setBuyCreditsAmount] = useState(null);
  const [queuedCredits, setQueuedCredits] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  // Distinguishes which flow opened the Stripe checkout, so the return page can
  // show the onboarding confirmation only for the self-signup deep link.
  const [checkoutSource, setCheckoutSource] = useState("upgrade");
  const [priceId, setPriceId] = useState("");
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [viewReportCostDialogOpen, setViewReportCostDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [loadingChange, setLoadingChange] = useState(false);
  const [loadingCurrentSub, setLoadingCurrentSub] = useState(false);
  const [loadingUsageData, setLoadingUsageData] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredUsageData, setFilteredUsageData] = useState([]);
  const [loadingExport, setLoadingExport] = useState(false);

  const canWriteStripe = AuthService.canWriteStripe();

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [usageData, setUsageData] = useState([]);
  const [usagePagination, setUsagePagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 5
  });

  const usageColumns = [
    { dataField: 'applicationName', text: 'Application Name' },
    { dataField: 'reportName', text: 'Report Name' },
    { dataField: 'requestBy', text: 'Requested by' },
    { dataField: 'requestDate', text: 'Date', formatter: (cell) => cell ? formatDate(cell) : 'N/A' },
    { dataField: 'creditAmount', text: 'Credit Amount' },
    { dataField: 'postTransactionBalance', text: 'Post Transaction Balance' },
    { dataField: 'status', text: 'Status' },
  ];

const handleCancelSubscription = () => {
  setLoadingCancel(true);

  cancelSubscription()
    .then(res => {
      setCancelDialogOpen(false);
      setLoadingCancel(false);
      window.location.reload()
    })
    .catch(
      err => { console.error("Error to cancel the subscription", err)
        setLoadingCancel(false);
    })
};


  const callToSwitchPlan = (plan) => {
    const user = JSON.parse(localStorage.getItem("user"));
    const payload = {
      priceId:plan.priceCode,
      userId: user.userId
    }

    switchSubscription(payload).then(res => {
        setChangePlanDialogOpen(false)
        window.location.reload()
    }).catch(err => {
      setChangePlanDialogOpen(false)
    })
  }




  const fetchSubscriptionDetails = useCallback(() => {
    setLoadingCurrentSub(true);
    subscriptionDetails()
        .then(res => {
          setSubscriptionData(res);
        })
        .catch(err => {
          console.error("Error fetching subscription details:", err);
        }).finally(() => {
      setLoadingCurrentSub(false);
    });
  }, []);

  const fetchUsageData = useCallback((pageNumber = 0, pageSize = 5, startDateParam = '', endDateParam = '') => {
    setLoadingUsageData(true);
    
    let queryParams = `pageSize=${pageSize}&pageNumber=${pageNumber}`;
    if (startDateParam) queryParams += `&startDate=${startDateParam}`;
    if (endDateParam) queryParams += `&endDate=${endDateParam}`;
    
    getCreditPackages(pageSize, pageNumber, startDateParam, endDateParam)
      .then(res => {
        const { response, headers } = res;
        const totalRecords = parseInt(headers['x-pagination-total-records']) || 0;
        const totalPages = parseInt(headers['x-pagination-total-pages']) || 1;
        
        const data = Array.isArray(response) ? response : [];
        setUsageData(data);
        setFilteredUsageData(data);
        setUsagePagination({
          currentPage: pageNumber,
          totalPages: totalPages,
          totalElements: totalRecords,
          pageSize: pageSize
        });
      })
      .catch(err => {
        console.error("Error fetching usage data:", err);
        setUsageData([]);
        setFilteredUsageData([]);
        setUsagePagination(prev => ({
          ...prev,
          totalElements: 0,
          totalPages: 1
        }));
      })
      .finally(() => {
        setLoadingUsageData(false);
      });
  }, []);

  const handleTableChange = useCallback((paginationParam) => {
    const { page, limit } = paginationParam;
    
    setUsagePagination(prev => ({
      ...prev,
      currentPage: page,
      pageSize: limit
    }));
    fetchUsageData(page, limit, startDate, endDate);
  }, [fetchUsageData, startDate, endDate]);

  const handleDateRangeFilter = useCallback(() => {
    fetchUsageData(0, usagePagination.pageSize, startDate, endDate);
  }, [fetchUsageData, startDate, endDate, usagePagination.pageSize]);

  const calculateInitialBalance = useCallback((data, startDateParam) => {
    if (!data || data.length === 0) return 0;
    
    const sortedData = [...data].sort((a, b) => new Date(a.requestDate) - new Date(b.requestDate));
    
    const firstTransaction = sortedData.find(item => {
      const itemDate = new Date(item.requestDate).toISOString().split('T')[0];
      return itemDate >= startDateParam;
    });
    
    if (firstTransaction) {
      const postBalance = parseInt(firstTransaction.postTransactionBalance) || 0;
      const creditAmount = parseInt(firstTransaction.creditAmount) || 0;
      return postBalance + creditAmount;
    }
    
    return 0;
  }, []);

  const calculateFinalBalance = useCallback((data, endDateParam) => {
    if (!data || data.length === 0) return 0;
    
    const sortedData = [...data].sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));
    
    if (!endDateParam) {
      const mostRecentTransaction = sortedData[0];
      return mostRecentTransaction ? parseInt(mostRecentTransaction.postTransactionBalance) || 0 : 0;
    }
    
    const lastTransaction = sortedData.find(item => {
      const itemDate = new Date(item.requestDate).toISOString().split('T')[0];
      return itemDate <= endDateParam;
    });
    
    return lastTransaction ? parseInt(lastTransaction.postTransactionBalance) || 0 : 0;
  }, []);

  const exportToPDF = useCallback(() => {
    setLoadingExport(true);
    
    const doc = new jsPDF();
    const initialBalance = calculateInitialBalance(filteredUsageData, startDate);
    const finalBalance = calculateFinalBalance(filteredUsageData, endDate);
    
    doc.setFontSize(18);
    doc.text(strings.BILLING.CREDIT_BALANCE_STATEMENT, 14, 22);
    
    doc.setFontSize(12);
    doc.text(`${strings.BILLING.PERIOD}: ${startDate || strings.BILLING.ALL_TIME} ${strings.BILLING.TO} ${endDate || strings.BILLING.PRESENT}`, 14, 35);
    doc.text(`${strings.BILLING.INITIAL_BALANCE}: ${initialBalance} ${strings.BILLING.CREDITS_UNIT}`, 14, 42);
    doc.text(`${strings.BILLING.FINAL_BALANCE}: ${finalBalance} ${strings.BILLING.CREDITS_UNIT}`, 14, 49);
    
    const tableData = filteredUsageData.map(item => [
      item.applicationName || '',
      item.reportName || '',
      item.requestBy || '',
      item.requestDate ? formatDate(item.requestDate) : '',
      item.creditAmount || '0',
      item.postTransactionBalance || '0',
      item.status || ''
    ]);
    
    doc.autoTable({
      head: [[strings.BILLING.APPLICATION, strings.BILLING.REPORT, strings.BILLING.REQUESTED_BY, strings.BILLING.DATE, strings.BILLING.CREDITS, strings.BILLING.BALANCE, strings.BILLING.STATUS]],
      body: tableData,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] }
    });
    
    doc.save(`credit-statement-${startDate || 'all'}-to-${endDate || 'present'}.pdf`);
    setLoadingExport(false);
  }, [filteredUsageData, startDate, endDate, calculateInitialBalance, calculateFinalBalance]);

  useEffect(() => {
    fetchSubscriptionDetails();
    fetchUsageData();
  }, [fetchSubscriptionDetails, fetchUsageData]);

  // Re-fetch plan + usage when the parent signals a billing change from the
  // Stripe confirmation modal (subscription activated / credits added).
  useEffect(() => {
    if (!refreshSignal) return;
    fetchSubscriptionDetails();
    fetchUsageData();
  }, [refreshSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  function findActivePlan() {
    return tiers?.find(plan => plan?.isActivePlan === true) || {assets: null,
      description: strings.BILLING.TRIAL_DESCRIPTION,
      id: null,
      interval: "monthly",
      isActivePlan: false,
      planId: null,
      priceAmount: "0",
      priceCode: null,
      pricePerCredit: 300,
      product: "Trial",
      savings: ""};
  }

  function isCheaperThanActive(candidatePlan) {
    const activePlan = findActivePlan();
    if (!activePlan || !candidatePlan) return false;
    return candidatePlan?.pricePerCredit > activePlan?.pricePerCredit;
  }

  // Deep-link /settings?tab=billing&plan=<slug>&credits=<n>: opens Change Plan first,
  // then Buy Credits after the switch (credits intent bridged via localStorage).
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current || !tiers?.length || !subscriptionData) return;
    const params = new URLSearchParams(window.location.search);
    const planReq = (params.get("plan") || "").trim().toLowerCase();
    const creditsRaw = (params.get("credits") || "").trim();
    const pendingCredits = localStorage.getItem("pendingCredits") || "";
    if (!planReq && !creditsRaw && !pendingCredits) return;

    // Free Starter deep link (post email-verification): show the one-time free-trial
    // welcome modal instead of a plan-switch/payment dialog — Starter is free, so there
    // is nothing to pay. Only for users without a paid subscription. Strip ?plan so a
    // refresh doesn't re-open it (the deep link is single-use).
    if (planReq === "starter" && !subscriptionData?.hasSubscriptionId) {
      deepLinkHandled.current = true;
      setWelcomeModalOpen(true);
      const cleaned = new URLSearchParams(window.location.search);
      cleaned.delete("plan");
      const qs = cleaned.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
      return;
    }

    deepLinkHandled.current = true;
    if (!canWriteStripe) { localStorage.removeItem("pendingCredits"); return; }

    // Normalize credits to a positive integer once; anything invalid becomes null.
    const parseCredits = (v) => {
      const n = Number(v);
      return Number.isInteger(n) && n > 0 ? n : null;
    };
    const urlCredits = parseCredits(creditsRaw);
    const carriedCredits = parseCredits(pendingCredits);
    // pendingCredits is single-use: if it was set but is unusable, drop it now so
    // it can't get stuck in localStorage (this effect only runs once per mount).
    if (pendingCredits && carriedCredits === null) localStorage.removeItem("pendingCredits");

    // 1) Plan first (if requested and actually switchable).
    if (planReq) {
      const slugify = (p) => (p?.product || "").toLowerCase().trim().replace(/\s+/g, "-");
      const match = tiers.find((t) =>
        slugify(t) === planReq ||
        `${t.id}`.toLowerCase() === planReq ||
        `${t.priceCode}`.toLowerCase() === planReq
      );
      if (match && !match.isActivePlan && !isCheaperThanActive(match)) {
        setSelectedPlan(match);
        setPriceId(match.priceCode);
        if (!subscriptionData?.hasSubscriptionId) {
          // Paid plan via deep link with no existing subscription → skip the Change Plan
          // confirmation and open the Stripe checkout directly (mirrors ChangePlanDialog onConfirm).
          if (urlCredits) localStorage.setItem("pendingCredits", String(urlCredits));
          setCheckoutSource("onboarding");
          setStripeDialogOpen(true);
        } else {
          // Existing subscriber → keep the confirmation step (it's a plan switch, not a new payment).
          // queuedCredits is persisted to localStorage only on confirm (see onConfirm).
          if (urlCredits) setQueuedCredits(urlCredits);
          setChangePlanDialogOpen(true);
        }
        return;
      }
    }

    // 2) Credits — from the URL param, or a pending intent left by a prior plan switch.
    const amount = urlCredits || carriedCredits;
    if (amount) {
      localStorage.removeItem("pendingCredits");
      setBuyCreditsAmount(amount);
      setBuyCreditsDialogOpen(true);
    }
  }, [tiers, subscriptionData]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <Box>
      <Card style={{ marginBottom: 32 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <CreditCardOutlinedIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">{strings.BILLING.YOUR_PLAN}</Typography>
            </Box>
          }
          action={
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                color="primary"
                style={{ marginRight: 8 }}
                onClick={() => setViewReportCostDialogOpen(true)}
              >
                {strings.BILLING.VIEW_REPORT_COST_TITLE}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setBuyCreditsDialogOpen(true)}
                disabled={!canWriteStripe || !subscriptionData?.hasSubscriptionId}
                startIcon={<Icon folder="icons" fileName="credits.svg" style={{display: "flex"}} />}
              >
                {strings.BILLING.BUY_MORE_CREDITS}
              </Button>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          {!loadingCurrentSub ?
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    {strings.BILLING.CURRENT_PLAN}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Typography variant="body1" style={{fontWeight: 500}}>
                      {subscriptionData?.currentPlan}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    {strings.BILLING.CREDITS}
                  </Typography>
                  <Typography variant="body1" style={{fontWeight: 500, marginTop: 8}}>
                    <span style={{color: 'green', fontWeight: 600, marginLeft: 4}}>
                  {subscriptionData?.creditsAvailable} {strings.BILLING.CREDITS_AVAILABLE}
                </span>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    {strings.BILLING.NEXT_BILLING}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <CalendarTodayOutlinedIcon style={{color: "#888", marginRight: 6}}/>
                    <Typography variant="body1" style={{fontWeight: 500}}>
                      {!!subscriptionData?.nextBilling ? formatDate(subscriptionData?.nextBilling) : strings.BILLING.NO_BILLING_SCHEDULED}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    {strings.BILLING.AUTO_REPLENISH}
                  </Typography>
                  <Box display="flex" alignItems="center" bgcolor="#f5f5f5" borderRadius={8} p={1}
                       border="1px solid #eee"
                       mt={1}>
                    <Typography variant="body2" style={{fontWeight: 500, color: '#888'}}>
                      {strings.BILLING.AUTO_REPLENISH_OFF_TEXT}
                    </Typography>
                  </Box>
                </Grid>
              </Grid> : <div style={{justifySelf: "center"}}><CircularProgress color='inherit' size={24}/></div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <TrendingUpOutlinedIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">{strings.BILLING.AVAILABLE_PLANS}</Typography>
            </Box>
          }
          subheader={strings.BILLING.PLANS_SUBHEADER}
        />
        <CardContent>
          { !!tiers ?
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4} key={0}>
              <Paper
                  elevation={1}
                  style={{
                    padding: 24,
                    border: "1px solid #eee",
                    position: "relative",
                    minHeight: 222,
                  }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, marginLeft: 8 }}>
                    {strings.BILLING.TRIAL}
                  </Typography>
                </Box>
                <Box>
                  <Box display="flex" alignItems="baseline">
                    <Typography variant="h5" style={{ fontWeight: 700 }}>
                      {strings.BILLING.TRIAL_PRICE}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginLeft: 4 }}>
                      {strings.BILLING.TRIAL_INTERVAL}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 4, width: "50%", marginBottom: 15, minHeight: 38 }}>
                    {strings.BILLING.TRIAL_DESCRIPTION}
                  </Typography>
                </Box>
                <Box>
                      <Button variant="outlined" fullWidth disabled>
                        {strings.BILLING.TRIAL_BUTTON_TEXT}
                      </Button>
                </Box>
              </Paper>
            </Grid>
            {tiers?.map((tier) => (
              <Grid item xs={12} md={6} lg={4} key={tier.id}>
                <Paper
                  elevation={tier.isCurrentTier ? 6 : 1}
                  style={{
                    padding: 24,
                    border: "1px solid #eee",
                    background: tier.isCurrentTier ? "#f5f7fa" : undefined,
                    position: "relative",
                    minHeight: 222,
                  }}
                >
                  {tier.savings && (
                    <Box className={styles.planBadgeSavings}>
                      {tier.savings}
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" mb={2}>
                    {React.isValidElement(tier.icon)
                      ? tier.icon
                      : tier.icon
                      ? React.createElement(tier.icon)
                      : null}
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginLeft: 8 }}>
                      {tier.product}
                    </Typography>
                  </Box>
                  <Box>
                    <Box display="flex" alignItems="baseline">
                      <Typography variant="h5" style={{ fontWeight: 700 }}>
                        {centsToUSD(tier?.priceAmount)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginLeft: 4 }}>
                        {"/"+tier.interval}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4, width: "50%", marginBottom: 15, minHeight: 38 }}>
                      {tier.description}
                    </Typography>
                  </Box>
                  <Box>
                    {tier.isActivePlan ? (
                      <Button variant="outlined" fullWidth disabled>
                        {strings.BILLING.CURRENT_PLAN_BUTTON}
                      </Button>
                    ) : (
                      <Button
                        variant={tier.isPopular ? "contained" : "outlined"}
                        color="primary"
                        fullWidth
                        disabled={!canWriteStripe || isCheaperThanActive(tier)}
                        onClick={() => {
                          setSelectedPlan(tier);
                          setPriceId(tier.priceCode)
                          setChangePlanDialogOpen(true);
                        }}
                      >
                        {tier.id === "starter" ? strings.BILLING.SWITCH_TO_STARTER : `${strings.BILLING.UPGRADE_TO} ${tier.product}`}
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
              : <div style={{justifySelf:"center"}}> <CircularProgress color='inherit' size={24}/></div>}
        </CardContent>
      </Card>

      <Card className='mt-4'>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <FilterListIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">{strings.BILLING.USAGE_CREDIT_STATEMENT}</Typography>
            </Box>
          }
          action={
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Box display="flex" flexDirection="column" alignItems="flex-start" style={{ marginRight: '8px' }}>
                <Typography variant="caption" style={{ marginBottom: '4px', fontSize: '12px' }}>
                  {strings.BILLING.START_DATE}
                </Typography>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder={strings.BILLING.START_DATE}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc'
                  }}
                />
              </Box>
              <Box display="flex" flexDirection="column" alignItems="flex-start" style={{ marginRight: '8px' }}>
                <Typography variant="caption" style={{ marginBottom: '4px', fontSize: '12px' }}>
                  {strings.BILLING.END_DATE}
                </Typography>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder={strings.BILLING.END_DATE}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc'
                  }}
                />
              </Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDateRangeFilter}
                style={{ marginRight: '8px', marginTop: 'auto' }}
              >
                {strings.BILLING.FILTER}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={exportToPDF}
                disabled={loadingExport || filteredUsageData.length === 0}
                startIcon={<GetAppOutlinedIcon />}
                style={{marginTop: 'auto'}}
              >
                {strings.BILLING.PDF}
              </Button>
            </Box>
          }
        />
        <Divider />
        <div className='p-2'>
          <TableComponent
            data={usageData}
            keyField='creditTransactionId'
            columns={usageColumns}
            sort={true}
            loading={loadingUsageData}
            showPagination={true}
            totalRecords={usagePagination.totalElements}
            totalPages={usagePagination.totalPages}
            currentPage={usagePagination.currentPage}
            paginationLimit={usagePagination.pageSize}
            onChangePagination={handleTableChange}
            useLocalPagination={false}
          />
        </div>
      </Card>

      <Card className='mt-4'>
        <div style={{ padding: '1rem 1rem 0 1rem' }}>
          <h5>{strings.BILLING.MANAGE_SUBSCRIPTION}</h5>
          <hr />
        </div>
        <div style={{ padding: '0 1rem 1rem 1rem' }}>
          {subscriptionData?.isActivePlan && findActivePlan().product !== strings.BILLING.TRIAL && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} mt={2}>
              <Typography variant="body1">{strings.BILLING.CANCEL_SUBSCRIPTION_PLAN_TEXT}</Typography>
              <Button variant="text" style={{ color: 'red' }} onClick={() => setCancelDialogOpen(true)}
                      disabled={!canWriteStripe}>
                {strings.BILLING.CANCEL_SUBSCRIPTION_BUTTON}
              </Button>
            </Box>
          )}

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body1" style={{ maxWidth: 400 }}>
              {strings.BILLING.NO_SUBSCRIPTION_TEXT}
            </Typography>
            <Button variant="text" style={{ color: 'red' }} onClick={()=> window.open("https://support.roxwrite.com/hc/en-us")}
                    disabled={!canWriteStripe}>
              {strings.BILLING.CONTACT_SUPPORT}
            </Button>
          </Box>
        </div>
      </Card>

      <BuyCreditsDialog
        open={buyCreditsDialogOpen}
        onClose={() => { setBuyCreditsDialogOpen(false); setBuyCreditsAmount(null); }}
        initialAmount={buyCreditsAmount}
        planName={findActivePlan()?.product}
        creditCost={subscriptionData?.creditCostInCents}
        creditsAvailable={subscriptionData?.creditsAvailable}
        fetchSubscriptionDetails={fetchSubscriptionDetails}
        fetchUsageData={fetchUsageData}
      />

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={(e,reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return; // prevent closing
          }
          setCancelDialogOpen(false)
        }}
        onCancelSubscription={() => handleCancelSubscription()}
        planName={findActivePlan()?.product}
        creditsAvailable={subscriptionData?.creditsAvailable}
        loading={loadingCancel}
      />

      <ChangePlanDialog
        open={changePlanDialogOpen}
        onClose={(e,reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return; // prevent closing
          }
          // Cancelling the plan switch abandons any queued credits deep-link step.
          localStorage.removeItem("pendingCredits")
          setQueuedCredits(null)
          setChangePlanDialogOpen(false)
        }}
        onConfirm={() => {
          setLoadingChange(true)
          // Persist the queued credits step now that the switch is confirmed.
          // queuedCredits is already a validated positive integer (see the deep-link effect).
          if (queuedCredits) localStorage.setItem("pendingCredits", String(queuedCredits))
          if (selectedPlan) {
            handleTierChange(selectedPlan);
          }

          if(!subscriptionData?.hasSubscriptionId){
            setCheckoutSource("upgrade")
            setStripeDialogOpen(true)
            setChangePlanDialogOpen(false)
          }
          else {
            callToSwitchPlan(selectedPlan)
          }
        }}
        currentPlan={findActivePlan()}
        loading={loadingChange}
        newPlan={selectedPlan || {}}
        creditsRemaining={subscriptionData?.creditsAvailable}
        nextBillingDate={subscriptionData?.nextBilling}
      />

      <ViewReportCostDialog
        open={viewReportCostDialogOpen}
        onClose={() => setViewReportCostDialogOpen(false)}
        planName={findActivePlan()?.product}
      />
      <StripeCheckoutBlade open={stripeDialogOpen} onClose={(e,reason)=> {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return; // prevent closing
        }
        setStripeDialogOpen(false)
      }} priceId={priceId} source={checkoutSource} planName={selectedPlan?.product} disableCall={subscriptionData?.hasSubscriptionId}/>
      <FreeTrialWelcomeModal
        open={welcomeModalOpen}
        onClose={() => setWelcomeModalOpen(false)}
        credits={TRIAL_INCLUDED_CREDITS}
      />
    </Box>
  );
}